// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Shared media-device helpers for the call backends + the call UI.

// enumerateInputs lists the available microphones + cameras, deduped by groupId.
// Android exposes several logical deviceIds per physical sensor (only some of which
// actually open), and desktop adds synthetic "default"/"communications" aliases — both
// produce the "4 cameras, 2 broken" picker. One entry per physical device fixes that.
// Labels are only populated once media permission is granted (i.e. during a call).
export async function enumerateInputs(): Promise<{ mics: MediaDeviceInfo[]; cameras: MediaDeviceInfo[] }> {
  if (!navigator.mediaDevices?.enumerateDevices) return { mics: [], cameras: [] };
  const all = await navigator.mediaDevices.enumerateDevices();
  const dedupe = (list: MediaDeviceInfo[]) => {
    const seen = new Set<string>();
    return list.filter((d) => {
      const key = d.groupId || d.deviceId;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const real = (d: MediaDeviceInfo) => d.deviceId !== "default" && d.deviceId !== "communications";
  return {
    mics: dedupe(all.filter((d) => d.kind === "audioinput" && real(d))),
    cameras: dedupe(all.filter((d) => d.kind === "videoinput" && real(d))),
  };
}

// enumerateOutputs lists audio OUTPUT devices (speakers / headsets) for setSinkId on
// web/desktop. Browsers expose these only after media permission (a call); some browsers
// (notably Firefox) don't support output selection at all and return an empty list.
export async function enumerateOutputs(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const all = await navigator.mediaDevices.enumerateDevices();
  const seen = new Set<string>();
  return all
    .filter((d) => d.kind === "audiooutput" && d.deviceId !== "communications")
    .filter((d) => {
      const key = d.groupId || d.deviceId;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

// outputLabel gives a friendly name for an audio output device.
export function outputLabel(d: MediaDeviceInfo, index: number): string {
  if (d.deviceId === "default") return d.label ? d.label.replace(/^Default\s*-?\s*/i, "Default · ") : "Default output";
  return d.label || `Speaker ${index + 1}`;
}

// cameraLabel gives a friendly name for a camera: "Front"/"Back" inferred from the
// (often cryptic) device label, else a 1-based number — never the raw "camera2 0,...".
export function cameraLabel(d: MediaDeviceInfo, index: number): string {
  const l = (d.label || "").toLowerCase();
  if (/front|user|face|selfie/.test(l)) return "Front camera";
  if (/back|rear|environment|world/.test(l)) return "Back camera";
  return d.label || `Camera ${index + 1}`;
}

export function micLabel(d: MediaDeviceInfo, index: number): string {
  return d.label || `Microphone ${index + 1}`;
}
